import { useAuth0 } from '@auth0/auth0-react';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { queryOptions } from '@tanstack/react-query';
import { AssessmentSelectDto, AssessmentType, AssessmentsSelectDto } from '../types/api.types.ts';
import { useGetUserSelectedCompanyId } from './users.query.ts';
import { useGetCurrentUser } from './users.query.ts';

export const useAssessmentsQueryOptions = (params: {
  technologyId?: string;
  /**
   * Filter by assessment type(s).
   */
  type?: AssessmentType[];
}) => {
  const { technologyId, type } = params;
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { data: user, isExpert, isIntel, isInnovator } = useGetCurrentUser();
  const selectedCompanyId = useGetUserSelectedCompanyId();
  const enabled = Boolean(
    isAuthenticated && (selectedCompanyId || technologyId) && (isExpert || isInnovator || isIntel),
  );

  return queryOptions<AssessmentSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const path = isInnovator
        ? '/v1/innovator/assessments'
        : isExpert
          ? '/v1/expert/assessments'
          : isIntel
            ? '/v1/intel/assessments'
            : undefined;

      if (!path) {
        throw new Error('User role not supported for assessment access');
      }
      const result = await apiGet<AssessmentsSelectDto>({
        path,
        query: {
          ...(type?.length ? { type } : {}),
          ...(technologyId ? { technologyId } : {}),
        },
      });

      return result.assessments;
    },
    queryKey: enabled ? queryKeys.assessments.getAssessments(type, technologyId) : queryKeys.placeholders.default,
  });
};
